/**
 * Staircases, built as stairs: treads you can see and stand on, rather than a
 * striped ribbon on the ground.
 *
 * Two kinds:
 *
 * - **On the ground** — most mapped steps. The tile says only that a line is
 *   steps, and the elevation is far too coarse to show the bank a flight
 *   climbs, so a flight rises as flights do — or as the ground does, where it
 *   is steeper — and the ground is cut and filled to meet it (`stairCorridors`,
 *   carved with the roads), so the paths at either end run on at its level.
 * - **Raised** — a mapped staircase whose `step_count` (from Overpass) climbs
 *   well above what the ground does: an outside stair up a building, a
 *   footbridge's approach, several storeys of a stairwell. Or one end at a
 *   footbridge's deck high over the ground there, which it climbs to, counted
 *   or not. It climbs steadily
 *   from its lower end by its count of steps, in steel on posts, with rails —
 *   a line that folds back on itself becomes a switchback of flights.
 *
 * Output is a vertex-coloured mesh in chunk metres, drawn with the bridge
 * structure, and the tread tops as surfaces to stand on.
 */

/** A mesh under construction, and the parts of it that can be stood on. */
export interface StairMesh {
  positions: number[];
  /** Linear RGB per vertex. */
  colors: number[];
  indices: number[];
  walkPositions: number[];
  walkIndices: number[];
}

export function newStairMesh(): StairMesh {
  return { positions: [], colors: [], indices: [], walkPositions: [], walkIndices: [] };
}

/** An sRGB hex as linear RGB. */
function linear(hex: number): [number, number, number] {
  const f = (c: number) => { const v = c / 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return [f((hex >> 16) & 255), f((hex >> 8) & 255), f(hex & 255)];
}

const CONCRETE = linear(0xb9b4aa);
const STEEL = linear(0x4a5158);
const GALVANISED = linear(0x8e959b);
const RAIL = linear(0xd9a53a);

/** A riser, metres: a comfortable step. */
const RISER = 0.18;
/** A riser for a flight whose height is ours to choose. */
const RISER_NORMAL = 0.16;
/** A tread's depth, metres. */
const TREAD = 0.3;
/** Handrail height over the treads. */
const RAIL_HEIGHT = 0.95;

type V3 = [number, number, number];

function quad(m: StairMesh, a: V3, b: V3, c: V3, d: V3, colour: readonly number[], walk = false): void {
  const base = m.positions.length / 3;
  m.positions.push(...a, ...b, ...c, ...d);
  for (let k = 0; k < 4; k++) m.colors.push(colour[0], colour[1], colour[2]);
  m.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  if (walk) {
    const w = m.walkPositions.length / 3;
    m.walkPositions.push(...a, ...b, ...c, ...d);
    m.walkIndices.push(w, w + 1, w + 2, w, w + 2, w + 3);
  }
}

/**
 * A block: the four corners of its top (in order round it) from `bottom` up
 * to `top`. Its top is walked on when `walk`.
 */
function block(m: StairMesh, corners: Array<[number, number]>, bottom: number, top: number, colour: readonly number[], walk: boolean): void {
  const t = corners.map(([x, z]) => [x, top, z] as V3);
  const b = corners.map(([x, z]) => [x, bottom, z] as V3);
  quad(m, t[0], t[1], t[2], t[3], colour, walk);
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    quad(m, b[i], b[j], t[j], t[i], colour);
  }
}

/** A square-section bar from `a` to `b`, `size` across: rails, stringers, posts. */
function bar(m: StairMesh, a: V3, b: V3, size: number, colour: readonly number[]): void {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-4) return;
  // Two directions across the bar: sideways on the ground, and up across it.
  let sx = -dz, sz = dx;
  const sl = Math.hypot(sx, sz);
  if (sl < 1e-6) { sx = 1; sz = 0; } else { sx /= sl; sz /= sl; }
  const h = size / 2;
  // The second: the bar's direction crossed with the first.
  const d = [dx / len, dy / len, dz / len];
  let ux = d[1] * sz, uy = d[2] * sx - d[0] * sz, uz = -d[1] * sx;
  const ul = Math.hypot(ux, uy, uz) || 1;
  ux /= ul; uy /= ul; uz /= ul;
  const corner = (p: V3, s: number, u: number): V3 => [p[0] + (sx * s + ux * u) * h, p[1] + uy * u * h, p[2] + (sz * s + uz * u) * h];
  const ring: Array<[number, number]> = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  for (let i = 0; i < 4; i++) {
    const [s0, u0] = ring[i], [s1, u1] = ring[(i + 1) % 4];
    quad(m, corner(a, s0, u0), corner(a, s1, u1), corner(b, s1, u1), corner(b, s0, u0), colour);
  }
}

/** Along a polyline of x,z pairs: its total length, and the point and heading at a distance. */
function walker(points: ArrayLike<number>) {
  const n = points.length / 2;
  const cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1] + Math.hypot(points[i * 2] - points[i * 2 - 2], points[i * 2 + 1] - points[i * 2 - 1]));
  const length = cum[n - 1];
  const at = (s: number): { x: number; z: number; dx: number; dz: number } => {
    let i = 1;
    while (i < n - 1 && cum[i] < s) i++;
    const seg = cum[i] - cum[i - 1] || 1;
    const t = Math.max(0, Math.min(1, (s - cum[i - 1]) / seg));
    const x0 = points[i * 2 - 2], z0 = points[i * 2 - 1], x1 = points[i * 2], z1 = points[i * 2 + 1];
    return { x: x0 + (x1 - x0) * t, z: z0 + (z1 - z0) * t, dx: (x1 - x0) / seg, dz: (z1 - z0) / seg };
  };
  return { length, at };
}

/** The four corners of a tread from `a` to `b` (centres), `half` wide either side. */
function treadCorners(a: { x: number; z: number }, b: { x: number; z: number }, half: number): Array<[number, number]> {
  const dx = b.x - a.x, dz = b.z - a.z, l = Math.hypot(dx, dz) || 1;
  const sx = (-dz / l) * half, sz = (dx / l) * half;
  return [[a.x + sx, a.z + sz], [b.x + sx, b.z + sz], [b.x - sx, b.z - sz], [a.x - sx, a.z - sz]];
}

/** How steep a flight climbs when the ground under it does not say: rise per metre. */
const FLIGHT_PITCH = 0.45;
/** Most a flight on the ground is made to climb beyond what the ground does, metres. */
const MOST_BANK = 4.5;
/**
 * The same on the laser ground model, which has the ground as it stands: a
 * flight on a level square climbs no more than a few steps over it. Made to
 * climb a pitch's worth, one in Brunnsparken ended 3 m over the street, and
 * the ground filled up to it stood in a mound across the square.
 */
const TRUE_GROUND_BANK = 0.5;
/** How far past each end the ground is levelled to meet the flight, metres. */
const LANDING_GROUND = 3;
/**
 * How far the land may fall along a flight's tagged way up before the tag is
 * taken as wrong and the land as right, metres; and how far over the land's
 * own climb a counted flight stands before it is raised in steel over it.
 * A 123-step flight down the hill from Örgryte old church is tagged `incline=up`
 * the wrong way: the ground falls 17 m along it. Taken at its word, it was
 * built climbing 21 m into the air over the falling land, on posts up to
 * 45 m tall. Its count overshoots the land's 17 m by a fifth, as counts of
 * shallow park steps do at 17 cm a step: that is a flight on the ground.
 */
const TAG_DROP = 1.2;
const RAISED_OVER = 1.25;

/** How a mapped staircase is built: its line from bottom to top, and how high each end is. */
export interface StairPlan {
  /** x,z pairs, bottom first. */
  points: number[];
  halfWidth: number;
  y0: number;
  y1: number;
  /** In steel over the ground rather than on it: a counted flight climbing well above it. */
  raised: boolean;
}

/**
 * A flight's line, bottom first, cut where it first reaches a deck's edge:
 * the point between the last vertex off the deck and the first on it where
 * `past` crosses zero, found by halving.
 */
function toDeckEdge(points: number[], deckAt: (x: number, z: number) => { h: number; past: number } | undefined): number[] {
  const on = (x: number, z: number) => (deckAt(x, z)?.past ?? Infinity) <= 0;
  for (let i = 2; i < points.length; i += 2) {
    if (!on(points[i], points[i + 1])) continue;
    if (on(points[i - 2], points[i - 1])) return points.slice(0, i);
    let lo = 0, hi = 1;
    for (let k = 0; k < 20; k++) {
      const mid = (lo + hi) / 2;
      if (on(points[i - 2] + (points[i] - points[i - 2]) * mid, points[i - 1] + (points[i + 1] - points[i - 1]) * mid)) hi = mid;
      else lo = mid;
    }
    const x = points[i - 2] + (points[i] - points[i - 2]) * lo, z = points[i - 1] + (points[i + 1] - points[i - 1]) * lo;
    // Too short a flight left over: keep the line as it was.
    return Math.hypot(x - points[0], z - points[1]) < 1 ? points : [...points.slice(0, i), x, z];
  }
  return points;
}

/** x,z pairs in the other order: the last point first. */
function reversed(points: ArrayLike<number>): number[] {
  const out: number[] = [];
  for (let i = points.length - 2; i >= 0; i -= 2) out.push(points[i], points[i + 1]);
  return out;
}

/**
 * Plan a mapped staircase. With OSM's count of its steps (`rise`, metres)
 * climbing well above what the ground does, it is raised; otherwise it is on
 * the ground, climbing the ground's rise or a normal flight's, the steeper.
 * Up is the way `up` says (1 along the line, -1 against), else uphill —
 * looking past each end where the line itself is level.
 */
export function planStair(points: ArrayLike<number>, halfWidth: number, rise: number, up: number,
  ground: (x: number, z: number) => number,
  /** Roofs, when known: a flight with one end beside a roof it could climb to goes up there. */
  roofAt?: (x: number, z: number, ceiling: number) => number | undefined,
  /** The ground as it stands (the laser ground model): a flight climbs what it does, and little more. */
  trueGround = false,
  /**
   * A footbridge's deck near a point: its height there, and how far the point
   * is past its edge (negative on it). A flight with one end at a deck climbs
   * to it, and stops at its edge.
   */
  deckAt?: (x: number, z: number) => { h: number; past: number } | undefined): StairPlan | undefined {
  const w = walker(points);
  if (w.length < 0.4) return undefined;
  const n = points.length;
  const g0 = ground(points[0], points[1]), g1 = ground(points[n - 2], points[n - 1]);
  // Up to a footbridge's deck: the map joins the steps to the path that
  // crosses it, never to the deck's height, and on the ground as it stands
  // the flight climbed the bank a little and stopped metres under it.
  if (deckAt !== undefined) {
    const d0 = deckAt(points[0], points[1]), d1 = deckAt(points[n - 2], points[n - 1]);
    const high0 = d0 !== undefined && d0.h > g0 + 1.2, high1 = d1 !== undefined && d1.h > g1 + 1.2;
    if (high0 !== high1) {
      const deck = high1 ? d1! : d0!;
      // The map runs the steps on to meet the path in the deck's middle: the
      // flight stops at the deck's edge, where it lands.
      const pts = toDeckEdge(high1 ? Array.from(points) : reversed(points), deckAt);
      return { points: pts, halfWidth, y0: (high1 ? g0 : g1) + 0.05, y1: deck.h, raised: true };
    }
  }
  // The tagged way up, against the land: where the land falls away along it
  // by more than a flight's grace, the tag is wrong (or the tile runs the
  // line the other way from the way it was tagged on), and the land is right.
  if (up !== 0 && (up > 0 ? g1 - g0 : g0 - g1) < -TAG_DROP) up = -up;
  if (up === 0 && roofAt !== undefined) {
    const atStart = roofNear(points[0], points[1], g1, w.length, roofAt) !== undefined;
    const atEnd = roofNear(points[n - 2], points[n - 1], g0, w.length, roofAt) !== undefined;
    if (atStart !== atEnd) up = atEnd ? 1 : -1;
  }
  if (up === 0) {
    up = g1 - g0;
    if (Math.abs(up) < 0.3) {
      // Level here: which way does the land go on past each end?
      const a = w.at(0), b = w.at(w.length);
      const past0 = ground(a.x - a.dx * 12, a.z - a.dz * 12), past1 = ground(b.x + b.dx * 12, b.z + b.dz * 12);
      up = (past1 - g1) - (past0 - g0) || 1;
    }
  }
  const forward = up > 0;
  const pts = forward ? Array.from(points) : reversed(points);
  const bottom = forward ? g0 : g1, top = forward ? g1 : g0;
  const ground_ = Math.max(0, top - bottom);
  // Raised where the count climbs well above the land, in all and in share:
  // a long flight up a hillside counts a little over what the land rises.
  if (Number.isFinite(rise) && rise > ground_ + 1.2 && rise > ground_ * RAISED_OVER) {
    return { points: pts, halfWidth, y0: bottom + 0.05, y1: bottom + 0.05 + rise, raised: true };
  }
  const bank = trueGround ? TRUE_GROUND_BANK : MOST_BANK;
  const flight = Math.min(bank, w.length * FLIGHT_PITCH);
  // On the laser ground model the land has the flight's climb already: its
  // count, a little over, is held to a few steps more than the land.
  const counted = trueGround ? Math.min(rise, ground_ + bank) : rise;
  const climb = Number.isFinite(rise) ? Math.max(ground_, counted) : Math.max(ground_, flight);
  return { points: pts, halfWidth, y0: bottom + 0.02, y1: bottom + 0.02 + climb, raised: false };
}

/**
 * The ground a flight on the ground needs, as road-profile corridor segments:
 * climbing with it, and level a little way past each end for the paths there.
 */
export function stairCorridors(plan: StairPlan): Array<{
  x0: number; z0: number; h0: number; x1: number; z1: number; h1: number; halfWidth: number; reach: number; stair: true;
}> {
  const w = walker(plan.points);
  const out: ReturnType<typeof stairCorridors> = [];
  const heightAt = (s: number) => plan.y0 + ((plan.y1 - plan.y0) * s) / w.length;
  const pieces = Math.max(1, Math.ceil(w.length / 2));
  const seg = (a: { x: number; z: number }, b: { x: number; z: number }, h0: number, h1: number) =>
    out.push({ x0: a.x, z0: a.z, h0, x1: b.x, z1: b.z, h1, halfWidth: plan.halfWidth + 0.3, reach: 0.25, stair: true });
  for (let k = 0; k < pieces; k++) {
    const s0 = (k * w.length) / pieces, s1 = ((k + 1) * w.length) / pieces;
    seg(w.at(s0), w.at(s1), heightAt(s0), heightAt(s1));
  }
  const a = w.at(0), b = w.at(w.length);
  seg({ x: a.x - a.dx * LANDING_GROUND, z: a.z - a.dz * LANDING_GROUND }, a, plan.y0, plan.y0);
  seg(b, { x: b.x + b.dx * LANDING_GROUND, z: b.z + b.dz * LANDING_GROUND }, plan.y1, plan.y1);
  return out;
}

/**
 * A flight on the ground, bottom first: `y0` to `y1` in even risers of about
 * a normal step — deeper treads where it is long and shallow, as a stepped
 * path in a park — each tread a block down into the ground under it.
 */
export function groundSteps(m: StairMesh, points: ArrayLike<number>, halfWidth: number, y0: number, y1: number,
  ground: (x: number, z: number) => number): void {
  const w = walker(points);
  if (w.length < 0.2) return;
  const rise = Math.max(0, y1 - y0);
  // As many risers as the climb takes, and never a tread shallower than a real one.
  const n = Math.max(1, Math.min(Math.round(rise / RISER_NORMAL), Math.floor(w.length / TREAD)));
  let last = -Infinity;
  for (let i = 0; i < n; i++) {
    const a = w.at((i * w.length) / n), b = w.at(((i + 1) * w.length) / n);
    const corners = treadCorners(a, b, halfWidth);
    let low = Infinity, high = -Infinity;
    for (const [x, z] of corners) { const g = ground(x, z); low = Math.min(low, g); high = Math.max(high, g); }
    // Never buried where the ground bulges between the ends, never lower than the tread before.
    const top = Math.max(y0 + (rise * (i + 1)) / n, Math.min(high, y1) + 0.02, last);
    last = top;
    block(m, corners, Math.min(low, top - 0.3) - 0.2, top, CONCRETE, true);
  }
}

/** How far from a flight's top a roof may be for the flight to be its way up, metres. */
const ROOF_REACH = 3.5;

/**
 * The roof a flight on the ground climbs to, if its top end is beside one:
 * the nearest roof within `ROOF_REACH` that stands 1–7 m over the flight's
 * foot — what an outside stair up to a car park's deck, or a terrace, is.
 */
export function roofAtTop(plan: StairPlan, y0: number, roofAt: (x: number, z: number, ceiling: number) => number | undefined):
  { x: number; z: number; y: number } | undefined {
  const n = plan.points.length;
  return roofNear(plan.points[n - 2], plan.points[n - 1], y0, walker(plan.points).length, roofAt);
}

/** Steepest a flight may be to climb to a roof: rise over its length, with a step's grace. */
const ROOF_PITCH = 0.85;

/**
 * The nearest roof within `ROOF_REACH` of (tx, tz) that a flight `length`
 * long from a foot at `y0` could climb to: over a metre up, and no steeper
 * than a stair — a one-metre flight is not the way up a three-metre wall.
 */
function roofNear(tx: number, tz: number, y0: number, length: number,
  roofAt: (x: number, z: number, ceiling: number) => number | undefined): { x: number; z: number; y: number } | undefined {
  const most = Math.min(7, length * ROOF_PITCH + 0.3);
  if (most < 1) return undefined;
  let best: { x: number; z: number; y: number; d: number } | undefined;
  for (let r = 0; r <= ROOF_REACH; r += 0.5) {
    const around = r === 0 ? 1 : Math.ceil((Math.PI * 2 * r) / 0.6);
    for (let k = 0; k < around; k++) {
      const a = (k / around) * Math.PI * 2;
      const x = tx + Math.cos(a) * r, z = tz + Math.sin(a) * r;
      const y = roofAt(x, z, y0 + most);
      if (y === undefined || y < y0 + 1) continue;
      if (best === undefined || r < best.d) best = { x, z, y, d: r };
    }
    if (best !== undefined) break;
  }
  return best;
}

/** A landing from a flight's top across onto the roof beside it, at the roof's height. */
export function roofLanding(m: StairMesh, top: { x: number; z: number }, roof: { x: number; z: number }, y: number,
  halfWidth: number, ground: (x: number, z: number) => number): void {
  const dx = roof.x - top.x, dz = roof.z - top.z, l = Math.hypot(dx, dz);
  // A little way onto the roof, past its edge.
  const over = l < 0.01 ? top : { x: roof.x + (dx / l) * 0.8, z: roof.z + (dz / l) * 0.8 };
  if (l < 0.01) return;
  const corners = treadCorners(top, over, halfWidth);
  let low = y - 0.3;
  for (const [x, z] of corners) low = Math.min(low, ground(x, z));
  block(m, corners, low - 0.2, y, CONCRETE, true);
}

/**
 * A raised flight: from `y0` at the polyline's start to `y1` at its end, a
 * steady climb by length, in open steel treads on stringers, with posts to
 * the ground and a rail each side, and a landing at the top.
 */
export function raisedSteps(m: StairMesh, points: ArrayLike<number>, halfWidth: number, y0: number, y1: number,
  ground: (x: number, z: number) => number): void {
  // Always built bottom to top.
  let pts: ArrayLike<number> = points;
  if (y1 < y0) {
    const r: number[] = [];
    for (let i = points.length - 2; i >= 0; i -= 2) r.push(points[i], points[i + 1]);
    pts = r;
    [y0, y1] = [y1, y0];
  }
  const w = walker(pts);
  if (w.length < 0.5) return;
  const rise = y1 - y0;
  const n = Math.max(1, Math.round(rise / RISER));
  const heightAt = (s: number) => y0 + (rise * s) / w.length;
  for (let i = 0; i < n; i++) {
    const a = w.at((i * w.length) / n), b = w.at(((i + 1) * w.length) / n);
    const top = y0 + (rise * (i + 1)) / n;
    block(m, treadCorners(a, b, halfWidth), top - 0.05, top, GALVANISED, true);
  }
  // Landing at the top, a metre on in the same direction.
  const end = w.at(w.length);
  const over = { x: end.x + end.dx * 1.1, z: end.z + end.dz * 1.1 };
  block(m, treadCorners(end, over, halfWidth + 0.1), y1 - 0.08, y1, GALVANISED, true);

  // Stringers, rails and posts along each side, a piece per metre or two.
  const pieces = Math.max(1, Math.ceil(w.length / 1.5));
  for (const side of [-1, 1]) {
    const edge = (s: number): V3 => {
      const p = w.at(s);
      return [p.x - p.dz * (halfWidth + 0.04) * side, heightAt(s), p.z + p.dx * (halfWidth + 0.04) * side];
    };
    for (let k = 0; k < pieces; k++) {
      const a = edge((k * w.length) / pieces), b = edge(((k + 1) * w.length) / pieces);
      bar(m, [a[0], a[1] - 0.12, a[2]], [b[0], b[1] - 0.12, b[2]], 0.14, STEEL);
      bar(m, [a[0], a[1] + RAIL_HEIGHT, a[2]], [b[0], b[1] + RAIL_HEIGHT, b[2]], 0.05, RAIL);
    }
    for (let k = 0; k <= pieces; k++) {
      const p = edge((k * w.length) / pieces);
      const foot = ground(p[0], p[2]);
      // Posts carry the flight where it is off the ground, and the rail.
      bar(m, [p[0], Math.min(foot, p[1]) - 0.1, p[2]], [p[0], p[1] + RAIL_HEIGHT, p[2]], 0.08, STEEL);
    }
  }
}
